import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useApp } from "../contexts/AppContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  ArrowLeft, Save, Users, Shield, Building2, MapPin,
  Eye, Edit, Trash, FilePlus, Download, Settings, Key
} from "lucide-react";
import { toast } from "sonner";

import { API_URL } from "../lib/utils";

const DEFAULT_PERMISSIONS = {
  employees_view: true,
  employees_create: false,
  employees_edit: false,
  employees_delete: false,
  schedules_view: true,
  schedules_create: false,
  schedules_edit: false,
  payrolls_view: true,
  payrolls_edit: false,
  payrolls_export: false,
  companies_view: true,
  companies_create: false,
  companies_edit: false,
  companies_delete: false
};

const PERMISSION_GROUPS = [
  {
    name: "Dipendenti",
    icon: Users,
    permissions: [
      { key: "employees_view", label: "Visualizza", icon: Eye },
      { key: "employees_create", label: "Crea", icon: FilePlus },
      { key: "employees_edit", label: "Modifica", icon: Edit },
      { key: "employees_delete", label: "Elimina", icon: Trash }
    ]
  },
  {
    name: "Orari/Presenze",
    icon: Settings,
    permissions: [
      { key: "schedules_view", label: "Visualizza", icon: Eye },
      { key: "schedules_create", label: "Inserisci", icon: FilePlus },
      { key: "schedules_edit", label: "Modifica", icon: Edit }
    ]
  },
  {
    name: "Stipendi",
    icon: Key,
    permissions: [
      { key: "payrolls_view", label: "Visualizza", icon: Eye },
      { key: "payrolls_edit", label: "Modifica", icon: Edit },
      { key: "payrolls_export", label: "Esporta PDF", icon: Download }
    ]
  },
  {
    name: "Aziende/Sedi",
    icon: Building2,
    permissions: [
      { key: "companies_view", label: "Visualizza", icon: Eye },
      { key: "companies_create", label: "Crea", icon: FilePlus },
      { key: "companies_edit", label: "Modifica", icon: Edit },
      { key: "companies_delete", label: "Elimina", icon: Trash }
    ]
  }
];

export default function UserFormPage() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const isEditing = !!userId;
  const { isAdmin } = useAuth();
  const { companies, locations, fetchCompanies, fetchLocations } = useApp();
  
  const [loading, setLoading] = useState(false);
  const [loadingUser, setLoadingUser] = useState(isEditing);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    role: "manager",
    allowed_companies: [],
    allowed_locations: [],
    permissions: { ...DEFAULT_PERMISSIONS },
    is_active: true
  });

  useEffect(() => {
    fetchCompanies();
    fetchLocations();
    if (isEditing) {
      loadUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, fetchCompanies, fetchLocations]);

  const loadUser = async () => {
    setLoadingUser(true);
    try {
      const response = await axios.get(`${API_URL}/api/users/${userId}`, { withCredentials: true });
      const user = response.data;
      setFormData({
        email: user.email,
        password: "",
        name: user.name,
        role: user.role,
        allowed_companies: user.allowed_companies || [],
        allowed_locations: user.allowed_locations || [],
        permissions: user.permissions || { ...DEFAULT_PERMISSIONS },
        is_active: user.is_active !== false
      });
    } catch (err) {
      toast.error("Errore nel caricamento utente");
      navigate("/admin");
    } finally {
      setLoadingUser(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.email) {
      toast.error("Nome ed email sono obbligatori");
      return;
    }
    if (!isEditing && !formData.password) {
      toast.error("Password obbligatoria per nuovo utente");
      return;
    }

    setLoading(true);
    try {
      if (isEditing) {
        await axios.put(`${API_URL}/api/users/${userId}`, {
          name: formData.name,
          role: formData.role,
          allowed_companies: formData.allowed_companies,
          allowed_locations: formData.allowed_locations,
          permissions: formData.permissions,
          is_active: formData.is_active
        }, { withCredentials: true });
        toast.success("Utente aggiornato");
      } else {
        await axios.post(`${API_URL}/api/users`, formData, { withCredentials: true });
        toast.success("Utente creato");
      }
      navigate("/admin");
    } catch (err) {
      toast.error("Errore: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const toggleCompanyPermission = (companyId) => {
    setFormData(prev => {
      const current = prev.allowed_companies || [];
      if (current.includes(companyId)) {
        return { ...prev, allowed_companies: current.filter(id => id !== companyId) };
      } else {
        return { ...prev, allowed_companies: [...current, companyId] };
      }
    });
  };

  const toggleLocationPermission = (locationId) => {
    setFormData(prev => {
      const current = prev.allowed_locations || [];
      if (current.includes(locationId)) {
        return { ...prev, allowed_locations: current.filter(id => id !== locationId) };
      } else {
        return { ...prev, allowed_locations: [...current, locationId] };
      }
    });
  };

  const togglePermission = (permKey) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permKey]: !prev.permissions[permKey]
      }
    }));
  };

  const selectAllCompanies = () => {
    setFormData(prev => ({ ...prev, allowed_companies: companies.map(c => c.id) }));
  };

  const deselectAllCompanies = () => {
    setFormData(prev => ({ ...prev, allowed_companies: [] }));
  };

  const selectAllLocations = () => {
    setFormData(prev => ({ ...prev, allowed_locations: locations.map(l => l.id) }));
  };

  const deselectAllLocations = () => {
    setFormData(prev => ({ ...prev, allowed_locations: [] }));
  };

  const selectAllPermissions = () => {
    const allTrue = {};
    Object.keys(DEFAULT_PERMISSIONS).forEach(key => { allTrue[key] = true; });
    setFormData(prev => ({ ...prev, permissions: allTrue }));
  };

  const deselectAllPermissions = () => {
    setFormData(prev => ({ ...prev, permissions: { ...DEFAULT_PERMISSIONS } }));
  };

  // Get locations filtered by selected companies
  const getFilteredLocations = () => {
    if (formData.allowed_companies.length === 0) return locations;
    return locations.filter(l => formData.allowed_companies.includes(l.company_id));
  };

  if (!isAdmin) {
    navigate("/dashboard");
    return null;
  }

  if (loadingUser) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-[#52525B] font-ibm-plex">Caricamento...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-form-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/admin")}
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold font-outfit tracking-tight text-[#09090B]">
              {isEditing ? "Modifica Utente" : "Nuovo Utente"}
            </h1>
            <p className="text-[#52525B] font-ibm-plex mt-1">
              {isEditing ? "Modifica i dati e i permessi dell'utente" : "Crea un nuovo utente con i relativi permessi"}
            </p>
          </div>
        </div>
        <Button
          onClick={handleSave}
          disabled={loading}
          className="bg-black hover:bg-gray-800 text-white font-ibm-plex"
          data-testid="save-user-button"
        >
          <Save className="w-4 h-4 mr-2" />
          {loading ? "Salvataggio..." : "Salva Utente"}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Basic Info */}
        <div className="space-y-6">
          <Card className="border border-[#E4E4E7]">
            <CardHeader>
              <CardTitle className="font-outfit text-lg">Informazioni Base</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="font-ibm-plex">Nome *</Label>
                <Input
                  data-testid="user-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="font-ibm-plex"
                  placeholder="Mario Rossi"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="font-ibm-plex">Email *</Label>
                <Input
                  data-testid="user-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="font-ibm-plex"
                  placeholder="mario@azienda.it"
                  disabled={isEditing}
                />
              </div>
              
              {!isEditing && (
                <div className="space-y-2">
                  <Label className="font-ibm-plex">Password *</Label>
                  <Input
                    data-testid="user-password-input"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="font-ibm-plex"
                    placeholder="••••••••"
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label className="font-ibm-plex">Ruolo *</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                  <SelectTrigger data-testid="user-role-select" className="font-ibm-plex">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Amministratore</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="operatore">Operatore</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t">
                <Label className="font-ibm-plex">Utente Attivo</Label>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  data-testid="user-active-switch"
                />
              </div>
            </CardContent>
          </Card>

          {formData.role === 'admin' && (
            <Card className="border border-green-200 bg-green-50">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-ibm-plex font-medium text-green-800">Amministratore</p>
                    <p className="text-sm text-green-600">Accesso completo a tutte le funzionalità</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Middle Column - Companies & Locations */}
        {formData.role !== 'admin' && (
          <div className="space-y-6">
            <Card className="border border-[#E4E4E7]">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-outfit text-lg flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Aziende Abilitate
                  </CardTitle>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" onClick={selectAllCompanies} className="text-xs h-7">
                      Tutte
                    </Button>
                    <Button variant="outline" size="sm" onClick={deselectAllCompanies} className="text-xs h-7">
                      Nessuna
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {companies.map(c => (
                    <label 
                      key={c.id} 
                      className="flex items-center gap-3 p-2 border rounded-md hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <Checkbox
                        checked={formData.allowed_companies.includes(c.id)}
                        onCheckedChange={() => toggleCompanyPermission(c.id)}
                      />
                      <span className="font-ibm-plex text-sm">{c.name}</span>
                    </label>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border border-[#E4E4E7]">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-outfit text-lg flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    Sedi Abilitate
                  </CardTitle>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" onClick={selectAllLocations} className="text-xs h-7">
                      Tutte
                    </Button>
                    <Button variant="outline" size="sm" onClick={deselectAllLocations} className="text-xs h-7">
                      Nessuna
                    </Button>
                  </div>
                </div>
                <CardDescription className="font-ibm-plex text-xs">
                  {formData.allowed_companies.length > 0 
                    ? "Filtrate per aziende selezionate" 
                    : "Tutte le sedi disponibili"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {getFilteredLocations().map(l => {
                    const company = companies.find(c => c.id === l.company_id);
                    return (
                      <label 
                        key={l.id} 
                        className="flex items-center gap-3 p-2 border rounded-md hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <Checkbox
                          checked={formData.allowed_locations.includes(l.id)}
                          onCheckedChange={() => toggleLocationPermission(l.id)}
                        />
                        <div className="flex-1">
                          <span className="font-ibm-plex text-sm block">{l.name}</span>
                          <span className="font-ibm-plex text-xs text-[#52525B]">{company?.name}</span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Right Column - Permissions */}
        {formData.role !== 'admin' && (
          <div className="space-y-6">
            <Card className="border border-[#E4E4E7]">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-outfit text-lg flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Permessi Funzionalità
                  </CardTitle>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" onClick={selectAllPermissions} className="text-xs h-7">
                      Tutti
                    </Button>
                    <Button variant="outline" size="sm" onClick={deselectAllPermissions} className="text-xs h-7">
                      Default
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {PERMISSION_GROUPS.map(group => {
                  const GroupIcon = group.icon;
                  return (
                    <div key={group.name} className="border rounded-lg p-3">
                      <h4 className="font-ibm-plex font-semibold text-sm mb-3 flex items-center gap-2">
                        <GroupIcon className="w-4 h-4 text-[#52525B]" />
                        {group.name}
                      </h4>
                      <div className="space-y-2">
                        {group.permissions.map(perm => {
                          const PermIcon = perm.icon;
                          return (
                            <label 
                              key={perm.key} 
                              className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                            >
                              <span className="flex items-center gap-2 text-sm font-ibm-plex">
                                <PermIcon className="w-4 h-4 text-[#52525B]" />
                                {perm.label}
                              </span>
                              <Switch
                                checked={formData.permissions[perm.key] || false}
                                onCheckedChange={() => togglePermission(perm.key)}
                              />
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Full width for admin role */}
        {formData.role === 'admin' && (
          <div className="lg:col-span-2">
            {/* Empty space for admin */}
          </div>
        )}
      </div>

      {/* Bottom Save Button */}
      <div className="flex justify-end pt-4 border-t">
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => navigate("/admin")}
            className="font-ibm-plex"
          >
            Annulla
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="bg-black hover:bg-gray-800 text-white font-ibm-plex"
            data-testid="save-user-button-bottom"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? "Salvataggio..." : isEditing ? "Salva Modifiche" : "Crea Utente"}
          </Button>
        </div>
      </div>
    </div>
  );
}
